import { ApiError, apiErrorDetail, refusalMessage } from "./api";
import { LeadConflictError } from "../services/leadConversionService";

/**
 * EVENT-2179 · operator-facing copy for the claim/convert route's refusal CODES.
 *
 * Read off `backend/api/routes/lead_conversion.py`, which is the ONLY route these
 * sentences describe. It refuses in three shapes and two of them carry a bare code
 * and nothing else:
 *
 *   - `:34`  403 `{"code": "admin_override_required"}`
 *   - `:47`  `{"code": <one of the seven below>}` — 404 for `lead_not_found`,
 *            503 for `lead_action_failed`, 409 for the rest
 *   - `:49`  409 `detail=result` — the RPC result, carrying `ownership_conflict`
 *            plus the owner's name
 *
 * There is no `message` key on the first two shapes, so `refusalMessage` returns
 * null for every one of them and, before this map, the `??` chain fell through to
 * `ApiError.message` — which is only `API error: <status>` (lib/api.ts:100). That
 * bare status code is what reached the operator.
 *
 * These sentences are deliberately NOT shared with the SMS composer. That route
 * (`backend/api/routes/sms.py`) refuses with plain strings and with objects that
 * carry `message`, so `refusalMessage` already has the server's own words there
 * and a code map would be dead weight keyed on a field it never sends.
 *
 * `ownership_conflict` is absent on purpose: `leadActionErrorMessage` names the
 * actual owner, which beats any sentence written here.
 */
export const LEAD_CLAIM_REFUSAL: Record<string, string> = {
  lead_not_found: "This lead no longer exists — refresh the list and try again.",
  lead_missing_required_identity:
    "This lead has no name or email yet, so it cannot be claimed. Add one and try again.",
  admin_override_required: "Only an admin can claim a lead for somebody else.",
  idempotency_payload_mismatch:
    "This claim was already sent with different details. Refresh the list and try again.",
  next_action_integrity_error:
    "The lead was claimed but its next step could not be created. Tell support before retrying.",
  conversion_integrity_error:
    "This lead could not be turned into a client. Tell support before retrying.",
  lead_action_failed: "The claim service did not answer. Try again in a moment.",
};

/**
 * What a refused claim/convert should SAY, for any error that path can throw.
 *
 * The precedence is #553's, rung for rung: the owner's name when the conflict is
 * the reason; then the server's own sentence if it ever sends one; then what the
 * CODE means; then, for a code we have never seen, still a sentence naming it
 * rather than a status number; then a sentence naming the status; and only for a
 * genuinely non-API failure — a timeout, a dropped connection — `error.message`,
 * which for those classes is the descriptive thing to show.
 *
 * BOTH conflict arms are kept, and they say the same sentence. `LeadConflictError`
 * is the arm the service is supposed to provide, but it cannot today:
 * `leadConversionService.ts:33` looks for `{` inside `error.message` to find the
 * body, and `ApiError.message` is only `API error: <status>` — the body has always
 * been a separate field — so `indexOf("{")` is -1 forever and that throw is
 * unreachable. The `detail.code` arm is therefore the one that actually fires
 * today. The dead arm stays because the service may yet be fixed for its five
 * callers, and a refusal must not change its words depending on which layer
 * noticed it. (Reported, not fixed here: this packet is the four leaking call
 * sites, and repairing the service changes behaviour for callers outside them.)
 */
export function leadActionErrorMessage(error: unknown, subject: string): string {
  const detail = apiErrorDetail(error);
  const conflict =
    error instanceof LeadConflictError
      ? error.result
      : detail?.code === "ownership_conflict"
        ? detail
        : null;
  if (conflict) {
    // The wording is the one these surfaces ALREADY shipped, character for
    // character. The conflict arm was never the leak — it was the only refusal on
    // this path that already read as a sentence — so rewording it would be an
    // unasked-for copy change, and LeadsPage.test.tsx pins it for the inbox.
    const who = conflict.owner_full_name || conflict.owner_user_id || "another locator";
    return `Already owned by ${who}. Open the lead to review.`;
  }

  const code = typeof detail?.code === "string" ? detail.code : null;
  const said =
    refusalMessage(error) ??
    (code
      ? (LEAD_CLAIM_REFUSAL[code] ??
        `${subject} could not be claimed — the server refused with "${code}". Tell support if it keeps happening.`)
      : null);

  return (
    said ??
    (error instanceof ApiError
      ? `${subject} could not be claimed — the server refused (${error.status}) without saying why. Tell support if it keeps happening.`
      : error instanceof Error
        ? error.message
        : String(error))
  );
}
